import {
  Document,
  Packer,
  Paragraph,
  TextRun,
  Table,
  TableRow,
  TableCell,
  HeadingLevel,
  WidthType,
  AlignmentType,
  ImageRun,
  BorderStyle,
  ExternalHyperlink,
  Header,
  Footer,
  TextWrappingType,
  VerticalPositionAlign,
  VerticalPositionRelativeFrom,
  HorizontalPositionAlign,
  HorizontalPositionRelativeFrom,
  LevelFormat
} from 'docx';
import {saveAs} from 'file-saver';

import {useConfigStore} from '../../../store/reportStore/configStore';

import {getFrameworkColor, formatPageNumber} from './formattingUtils';
import {generateWatermarkImage} from './watermarkGenerator';

const inchesToTwip = (inches: number): number => Math.round(inches * 1440);
const normalizeInches = (value: any, fallback: number): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;

  return Math.max(0, Math.min(3, parsed));
};

// Map of standard CSS colors to hex codes
const CSS_COLOR_MAP: Record<string, string> = {
  aliceblue: 'f0f8ff',
  antiquewhite: 'faebd7',
  aqua: '00ffff',
  aquamarine: '7fffd4',
  azure: 'f0ffff',
  beige: 'f5f5dc',
  bisque: 'ffe4c4',
  black: '000000',
  blanchedalmond: 'ffebcd',
  blue: '0000ff',
  blueviolet: '8a2be2',
  brown: 'a52a2a',
  burlywood: 'deb887',
  cadetblue: '5f9ea0',
  chartreuse: '7fff00',
  chocolate: 'd2691e',
  coral: 'ff7f50',
  cornflowerblue: '6495ed',
  cornsilk: 'fff8dc',
  crimson: 'dc143c',
  cyan: '00ffff',
  darkblue: '00008b',
  darkcyan: '008b8b',
  darkgoldenrod: 'b8860b',
  darkgray: 'a9a9a9',
  darkgrey: 'a9a9a9',
  darkgreen: '006400',
  darkkhaki: 'bdb76b',
  darkmagenta: '8b008b',
  darkolivegreen: '556b2f',
  darkorange: 'ff8c00',
  darkorchid: '9932cc',
  darkred: '8b0000',
  darksalmon: 'e9967a',
  darkseagreen: '8fbc8f',
  darkslateblue: '483d8b',
  darkslategray: '2f4f4f',
  darkslategrey: '2f4f4f',
  darkturquoise: '00ced1',
  darkviolet: '9400d3',
  deeppink: 'ff1493',
  deepskyblue: '00bfff',
  dimgray: '696969',
  dimgrey: '696969',
  dodgerblue: '1e90ff',
  firebrick: 'b22222',
  floralwhite: 'fffaf0',
  forestgreen: '228b22',
  fuchsia: 'ff00ff',
  gainsboro: 'dcdcdc',
  ghostwhite: 'f8f8ff',
  gold: 'ffd700',
  goldenrod: 'daa520',
  gray: '808080',
  grey: '808080',
  green: '008000',
  greenyellow: 'adff2f',
  honeydew: 'f0fff0',
  hotpink: 'ff69b4',
  indianred: 'cd5c5c',
  indigo: '4b0082',
  ivory: 'fffff0',
  khaki: 'f0e68c',
  lavender: 'e6e6fa',
  lavenderblush: 'fff0f5',
  lawngreen: '7cfc00',
  lemonchiffon: 'fffacd',
  lightblue: 'add8e6',
  lightcoral: 'f08080',
  lightcyan: 'e0ffff',
  lightgoldenrodyellow: 'fafad2',
  lightgray: 'd3d3d3',
  lightgrey: 'd3d3d3',
  lightgreen: '90ee90',
  lightpink: 'ffb6c1',
  lightsalmon: 'ffa07a',
  lightseagreen: '20b2aa',
  lightskyblue: '87cefa',
  lightslategray: '778899',
  lightslategrey: '778899',
  lightsteelblue: 'b0c4de',
  lightyellow: 'ffffe0',
  lime: '00ff00',
  limegreen: '32cd32',
  linen: 'faf0e6',
  magenta: 'ff00ff',
  maroon: '800000',
  mediumaquamarine: '66cdaa',
  mediumblue: '0000cd',
  mediumorchid: 'ba55d3',
  mediumpurple: '9370db',
  mediumseagreen: '3cb371',
  mediumslateblue: '7b68ee',
  mediumspringgreen: '00fa9a',
  mediumturquoise: '48d1cc',
  mediumvioletred: 'c71585',
  midnightblue: '191970',
  mintcream: 'f5fffa',
  mistyrose: 'ffe4e1',
  moccasin: 'ffe4b5',
  navajowhite: 'ffdead',
  navy: '000080',
  oldlace: 'fdf5e6',
  olive: '808000',
  olivedrab: '6b8e23',
  orange: 'ffa500',
  orangered: 'ff4500',
  orchid: 'da70d6',
  palegoldenrod: 'eee8aa',
  palegreen: '98fb98',
  paleturquoise: 'afeeee',
  palevioletred: 'db7093',
  papayawhip: 'ffefd5',
  peachpuff: 'ffdab9',
  peru: 'cd853f',
  pink: 'ffc0cb',
  plum: 'dda0dd',
  powderblue: 'b0e0e6',
  purple: '800080',
  rebeccapurple: '663399',
  red: 'ff0000',
  rosybrown: 'bc8f8f',
  royalblue: '4169e1',
  saddlebrown: '8b4513',
  salmon: 'fa8072',
  sandybrown: 'f4a460',
  seagreen: '2e8b57',
  seashell: 'fff5ee',
  sienna: 'a0522d',
  silver: 'c0c0c0',
  skyblue: '87ceeb',
  slateblue: '6a5acd',
  slategray: '708090',
  slategrey: '708090',
  snow: 'fffafa',
  springgreen: '00ff7f',
  steelblue: '4682b4',
  tan: 'd2b48c',
  teal: '008080',
  thistle: 'd8bfd8',
  tomato: 'ff6347',
  turquoise: '40e0d0',
  violet: 'ee82ee',
  wheat: 'f5deb3',
  white: 'ffffff',
  whitesmoke: 'f5f5f5',
  yellow: 'ffff00',
  yellowgreen: '9acd32'
};

// Helper to sanitize and expand hex colors for Docx
const sanitizeHexColor = (color: string): string | undefined => {
  if (!color) return undefined;

  const lowerColor = color.toLowerCase();
  if (CSS_COLOR_MAP[lowerColor]) {
    return CSS_COLOR_MAP[lowerColor];
  }

  let hex = color.replace('#', '');

  // Fallback for rgb/rgba (Word export primarily uses hex)
  if (hex.startsWith('rgb')) return '000000';

  // Expand 3-digit hex to 6-digit hex
  if (hex.length === 3) {
    hex = hex
      .split('')
      .map((char) => char + char)
      .join('');
  } else if (hex.length === 4) {
    // e.g. #rgba
    hex = hex
      .slice(0, 3)
      .split('')
      .map((char) => char + char)
      .join('');
  } else if (hex.length === 8) {
    // e.g. #rrggbbaa
    hex = hex.slice(0, 6);
  }

  return hex;
};

// Helper function to crop image using Canvas
const getCroppedImageBuffer = async (
  url: string,
  crop: any
): Promise<ArrayBuffer> => {
  return await new Promise((resolve, reject) => {
    try {
      const image = new Image();
      image.crossOrigin = 'Anonymous';
      image.onload = () => {
        try {
          const canvas = document.createElement('canvas');
          const ctx = canvas.getContext('2d');
          if (!ctx) {
            reject(new Error('Could not get canvas context'));

            return;
          }

          const width = image.naturalWidth;
          const height = image.naturalHeight;

          const t = crop.top || 0;
          const l = crop.left || 0;
          const b = crop.bottom || 0;
          const r = crop.right || 0;

          const sourceX = (l / 100) * width;
          const sourceY = (t / 100) * height;
          const sourceWidth = width - ((l + r) / 100) * width;
          const sourceHeight = height - ((t + b) / 100) * height;

          canvas.width = sourceWidth;
          canvas.height = sourceHeight;

          ctx.drawImage(
            image,
            sourceX,
            sourceY,
            sourceWidth,
            sourceHeight,
            0,
            0,
            sourceWidth,
            sourceHeight
          );

          canvas.toBlob((resultBlob) => {
            if (resultBlob) {
              resultBlob.arrayBuffer().then(resolve).catch(reject);
            } else {
              reject(new Error('Canvas toBlob failed'));
            }
          }, 'image/png');
        } catch (err) {
          reject(err);
        }
      };
      image.onerror = (err) => {
        reject(err);
      };
      image.src = url;
    } catch (err) {
      reject(err);
    }
  });
};

// Helper to process text nodes with formatting
const processTextNode = (node: any): TextRun => {
  const textRunOptions: any = {
    text: node.text
  };

  if (node.bold) textRunOptions.bold = true;
  if (node.italic) textRunOptions.italics = true;
  if (node.underline) textRunOptions.underline = {};
  if (node.color) {
    // Docx expects hex without hash and must be 6 digits
    textRunOptions.color = sanitizeHexColor(node.color);
  }
  // Docx 'highlight' expects specific Enum values (e.g. "yellow").
  // It does NOT support Hex codes. We must use 'shading' for custom colors.
  // if (node.highlight) textRunOptions.highlight = node.highlight; // REMOVED to prevent errors with Hex codes

  if (node.highlight) {
    textRunOptions.shading = {
      type: 'clear', // ShadingType.CLEAR
      fill: sanitizeHexColor(node.highlight) // Sanitize Hex
    };
  }
  if (node.fontSize) textRunOptions.size = node.fontSize * 2; // docx uses half-points
  if (node.fontFamily) textRunOptions.font = node.fontFamily;

  return new TextRun(textRunOptions);
};

// Helper to process children of a block
const processChildren = (
  children: any[] = [],
  pageIndex: number = 0
): Array<TextRun | ExternalHyperlink> => {
  return children.map((child) => {
    if (child.text !== undefined) {
      return processTextNode(child);
    }

    if (child.type === 'page-number') {
      return new TextRun({
        text: formatPageNumber(pageIndex, child.format || '123'),
        ...(child.style || {})
      });
    }

    // Handle links
    if (child.type === 'link') {
      return new ExternalHyperlink({
        children: child.children.map((grandchild: any) => {
          // Usually links have text children. Ensure they look like links (blue, underlined)
          // if the editor doesn't explicitly style them.
          // If the editor node has style, use it.
          if (grandchild.text !== undefined) {
            return new TextRun({
              text: grandchild.text,
              style: 'Hyperlink' // Uses Word's built-in Hyperlink style
            });
          }

          return processTextNode(grandchild); // Fallback
        }),
        link: child.url
      });
    }

    // Nested blocks processing could go here if needed
    return new TextRun({text: ''});
  });
};

const getParagraphSpacing = (
  node: any,
  after: number
): {after: number; line: number} => {
  const lineHeight = Math.max(1, Number(node?.lineHeight ?? 1.5));

  return {
    after,
    line: Math.round(lineHeight * 240)
  };
};

export const exportToWord = async (
  content: any[],
  filename = 'document.docx',
  watermark: any = null,
  options: any = {}
): Promise<Blob> => {
  const children: any[] = [];
  const numberingConfigs: any[] = [];
  let listCounter = 0;

  const config = useConfigStore.getState();
  const pageOrientation = options.pageOrientation || config.pageOrientation;
  const isLandscape = pageOrientation === 'landscape';
  const widthTwips = inchesToTwip(isLandscape ? 11.69 : 8.27);
  const heightTwips = inchesToTwip(isLandscape ? 8.27 : 11.69);

  const rawMargins = options.pageMargins || {};
  const margins = {
    top: normalizeInches(rawMargins.top, 0.5),
    right: normalizeInches(rawMargins.right, 0.5),
    bottom: normalizeInches(rawMargins.bottom, 0.5),
    left: normalizeInches(rawMargins.left, 0.5)
  };

  const watermarkHeaderChildren: any[] = [];
  if (watermark?.text) {
    try {
      const watermarkBuffer = (await generateWatermarkImage(
        watermark.text,
        watermark
      )) as any;

      watermarkHeaderChildren.push(
        new Paragraph({
          children: [
            new ImageRun({
              data: watermarkBuffer,
              transformation: {
                width: 800,
                height: 800
              },
              floating: {
                zIndex: 0, // Behind text
                horizontalPosition: {
                  relative: HorizontalPositionRelativeFrom.PAGE,
                  align: HorizontalPositionAlign.CENTER
                },
                verticalPosition: {
                  relative: VerticalPositionRelativeFrom.PAGE,
                  align: VerticalPositionAlign.CENTER
                },
                behindDocument: true,
                wrap: {
                  type: TextWrappingType.NONE
                }
              }
            } as any)
          ]
        }),
        new Paragraph({text: ''})
      );
    } catch (e) {
      console.error('Failed to generate watermark image', e);
      watermarkHeaderChildren.push(
        new Paragraph({
          children: [
            new TextRun({
              text: watermark.text,
              color: 'CCCCCC',
              size: (watermark.fontSize || 40) * 2,
              bold: true
            })
          ],
          alignment: AlignmentType.CENTER
        }),
        new Paragraph({text: ''})
      );
    }
  }

  const processBlockNode = async (
    node: any,
    inheritedStyle: any = {},
    pageIndex: number = 0
  ): Promise<any> => {
    switch (node.type) {
      case 'heading-one':
        return new Paragraph({
          text: node.children[0]?.text || '',
          heading: HeadingLevel.HEADING_1,
          alignment: node.align
            ? AlignmentType[
                node.align.toUpperCase() as keyof typeof AlignmentType
              ]
            : AlignmentType.LEFT,
          spacing: getParagraphSpacing(node, 200)
        });

      case 'heading-two':
        return new Paragraph({
          text: node.children[0]?.text || '',
          heading: HeadingLevel.HEADING_2,
          alignment: node.align
            ? AlignmentType[
                node.align.toUpperCase() as keyof typeof AlignmentType
              ]
            : AlignmentType.LEFT,
          spacing: getParagraphSpacing(node, 150)
        });

      case 'block-quote':
        return new Paragraph({
          children: processChildren(node.children, pageIndex),
          indent: {left: 720}, // 0.5 inch
          border: {
            left: {
              color: 'dadce0',
              space: 1,
              style: BorderStyle.SINGLE,
              size: 24 // 3pt
            }
          },
          spacing: getParagraphSpacing(node, 200)
        });

      case 'image':
        try {
          let buffer: ArrayBuffer;
          // Check if node has crop data
          if (
            node.crop &&
            (node.crop.top ||
              node.crop.left ||
              node.crop.bottom ||
              node.crop.right)
          ) {
            try {
              buffer = await getCroppedImageBuffer(node.url, node.crop);
            } catch (cropErr) {
              console.warn('Cropping failed, using original:', cropErr);
              const response = await fetch(node.url);
              buffer = await response.arrayBuffer();
            }
          } else {
            const response = await fetch(node.url);
            buffer = await response.arrayBuffer();
          }

          const imgWidth = node.width ? parseInt(node.width, 10) : 400;
          const imgHeight = node.height ? parseInt(node.height, 10) : 300;
          const imageRunOptions: any = {
            data: buffer,
            transformation: {
              width: imgWidth,
              height: imgHeight
            }
          };

          if (node.float === 'left' || node.float === 'right') {
            const align =
              node.float === 'left'
                ? HorizontalPositionAlign.LEFT
                : HorizontalPositionAlign.RIGHT;
            imageRunOptions.floating = {
              horizontalPosition: {
                relative: HorizontalPositionRelativeFrom.MARGIN,
                align
              },
              verticalPosition: {
                relative: VerticalPositionRelativeFrom.PARAGRAPH,
                align: VerticalPositionAlign.TOP
              },
              wrap: {
                type: TextWrappingType.SQUARE
              },
              behindDocument: false,
              allowOverlap: true,
              margins: {
                top: 90,
                right: node.float === 'left' ? 180 : 90,
                bottom: 90,
                left: node.float === 'right' ? 180 : 90
              }
            };
          }

          return new Paragraph({
            children: [new ImageRun(imageRunOptions)],
            alignment: node.align
              ? AlignmentType[
                  node.align.toUpperCase() as keyof typeof AlignmentType
                ]
              : AlignmentType.LEFT,
            spacing: getParagraphSpacing(node, 200)
          });
        } catch (e) {
          console.error('Failed to load image for export', e);

          return new Paragraph({text: '[Image Upload Failed]'});
        }

      case 'placeholder-image':
        const pWidth = node.width ? parseInt(node.width, 10) * 15 : 3000;
        const pHeight = node.height ? parseInt(node.height, 10) * 15 : 1500;

        const tableOptions: any = {
          rows: [
            new TableRow({
              children: [
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: `[${(
                            node.placeholderType || 'IMAGE'
                          ).toUpperCase()} PLACEHOLDER]`,
                          bold: true,
                          color: '888888'
                        })
                      ],
                      alignment: AlignmentType.CENTER
                    })
                  ],
                  borders: {
                    top: {
                      style: BorderStyle.DOTTED,
                      size: 6,
                      space: 1,
                      color: '888888'
                    },
                    bottom: {
                      style: BorderStyle.DOTTED,
                      size: 6,
                      space: 1,
                      color: '888888'
                    },
                    left: {
                      style: BorderStyle.DOTTED,
                      size: 6,
                      space: 1,
                      color: '888888'
                    },
                    right: {
                      style: BorderStyle.DOTTED,
                      size: 6,
                      space: 1,
                      color: '888888'
                    }
                  },
                  width: {size: pWidth, type: WidthType.DXA},
                  verticalAlign: 'center' as any
                })
              ],
              height: {value: pHeight, rule: 'exact'}
            })
          ],
          width: {size: pWidth, type: WidthType.DXA},
          alignment: node.align
            ? AlignmentType[
                node.align.toUpperCase() as keyof typeof AlignmentType
              ]
            : AlignmentType.CENTER
        };

        if (node.float === 'left') {
          tableOptions.float = {
            horizontalAnchor: 'margin', // TableAnchorType.MARGIN equivalent string representation docx uses optionally
            verticalAnchor: 'text', // TableAnchorType.TEXT
            relativeHorizontalPosition: 'left', // RelativeHorizontalPosition.LEFT
            relativeVerticalPosition: 'paragraph', // RelativeVerticalPosition.PARAGRAPH
            leftFromText: 100,
            rightFromText: 100,
            topFromText: 100,
            bottomFromText: 100
          };
        } else if (node.float === 'right') {
          tableOptions.float = {
            horizontalAnchor: 'margin',
            verticalAnchor: 'text',
            relativeHorizontalPosition: 'right',
            relativeVerticalPosition: 'paragraph',
            leftFromText: 100,
            rightFromText: 100,
            topFromText: 100,
            bottomFromText: 100
          };
        }

        return new Table(tableOptions);

      case 'shape':
        const sWidth = node.width ? parseInt(node.width, 10) * 15 : 1800;
        const sHeight = node.height ? parseInt(node.height, 10) * 15 : 1200;
        const shapeLabel = `[${(
          node.shapeType || 'shape'
        ).toUpperCase()} SHAPE]`;
        const shapeTableOptions: any = {
          rows: [
            new TableRow({
              children: [
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: shapeLabel,
                          bold: true,
                          color: '1a73e8'
                        })
                      ],
                      alignment: AlignmentType.CENTER
                    })
                  ],
                  borders: {
                    top: {
                      style: BorderStyle.SINGLE,
                      size: 8,
                      space: 1,
                      color: '1a73e8'
                    },
                    bottom: {
                      style: BorderStyle.SINGLE,
                      size: 8,
                      space: 1,
                      color: '1a73e8'
                    },
                    left: {
                      style: BorderStyle.SINGLE,
                      size: 8,
                      space: 1,
                      color: '1a73e8'
                    },
                    right: {
                      style: BorderStyle.SINGLE,
                      size: 8,
                      space: 1,
                      color: '1a73e8'
                    }
                  },
                  shading: {fill: 'e8f0fe', type: 'clear'},
                  width: {size: sWidth, type: WidthType.DXA},
                  verticalAlign: 'center' as any
                })
              ],
              height: {value: sHeight, rule: 'exact'}
            })
          ],
          width: {size: sWidth, type: WidthType.DXA},
          alignment: node.align
            ? AlignmentType[
                node.align.toUpperCase() as keyof typeof AlignmentType
              ]
            : AlignmentType.CENTER
        };

        if (node.float === 'left') {
          shapeTableOptions.float = {
            horizontalAnchor: 'margin',
            verticalAnchor: 'text',
            relativeHorizontalPosition: 'left',
            relativeVerticalPosition: 'paragraph',
            leftFromText: 100,
            rightFromText: 120,
            topFromText: 80,
            bottomFromText: 80
          };
        } else if (node.float === 'right') {
          shapeTableOptions.float = {
            horizontalAnchor: 'margin',
            verticalAnchor: 'text',
            relativeHorizontalPosition: 'right',
            relativeVerticalPosition: 'paragraph',
            leftFromText: 120,
            rightFromText: 100,
            topFromText: 80,
            bottomFromText: 80
          };
        }

        return new Table(shapeTableOptions);

      case 'table':
        const tableRows = [];
        for (const row of node.children) {
          const cells = [];
          for (const cell of row.children) {
            cells.push(
              new TableCell({
                children: [
                  new Paragraph({
                    children: processChildren(cell.children, pageIndex)
                  })
                ],
                width: {
                  size: 100 / row.children.length,
                  type: WidthType.PERCENTAGE
                },
                shading: cell.backgroundColor
                  ? {
                      fill: sanitizeHexColor(cell.backgroundColor),
                      type: 'clear' // ShadingType.CLEAR
                    }
                  : undefined
              })
            );
          }
          tableRows.push(new TableRow({children: cells}));
        }

        return new Table({
          rows: tableRows,
          width: {size: 100, type: WidthType.PERCENTAGE}
        });

      case 'bulleted-list':
        const bulletListItems: Paragraph[] = [];
        if (Array.isArray(node.children)) {
          for (const item of node.children) {
            if (item.type === 'list-item') {
              bulletListItems.push(
                new Paragraph({
                  children: processChildren(item.children, pageIndex),
                  bullet: {level: 0},
                  alignment: item.align
                    ? AlignmentType[
                        item.align.toUpperCase() as keyof typeof AlignmentType
                      ]
                    : AlignmentType.LEFT,
                  spacing: getParagraphSpacing(item, 40)
                })
              );
            }
          }
        }

        return bulletListItems;

      case 'numbered-list':
        listCounter++;
        const listRef = `list-numbering-${listCounter}`;
        numberingConfigs.push({
          reference: listRef,
          levels: [
            {
              level: 0,
              format: LevelFormat.DECIMAL,
              text: '%1.',
              alignment: AlignmentType.START,
              style: {
                paragraph: {
                  indent: {left: 720, hanging: 360}
                }
              }
            }
          ]
        });

        const numberedListItems: Paragraph[] = [];
        if (Array.isArray(node.children)) {
          for (const item of node.children) {
            if (item.type === 'list-item') {
              numberedListItems.push(
                new Paragraph({
                  children: processChildren(item.children, pageIndex),
                  numbering: {reference: listRef, level: 0},
                  alignment: item.align
                    ? AlignmentType[
                        item.align.toUpperCase() as keyof typeof AlignmentType
                      ]
                    : AlignmentType.LEFT,
                  spacing: getParagraphSpacing(item, 40)
                })
              );
            }
          }
        }

        return numberedListItems;

      case 'columns-container':
        const columnNodes =
          Array.isArray(node.children) && node.children.length > 0
            ? node.children
            : [
                {
                  type: 'column',
                  children: [{type: 'paragraph', children: [{text: ''}]}]
                }
              ];
        const explicitWidths = Array.isArray(node.columnWidths)
          ? node.columnWidths
          : [];
        const totalExplicit = explicitWidths.reduce(
          (sum: number, width: any) => sum + Math.max(0, Number(width) || 0),
          0
        );
        const columnCells = [];
        for (const col of columnNodes) {
          const colChildren = (
            Array.isArray(col.children) ? col.children : []
          ).filter(
            (child: any) =>
              child &&
              child.type !== 'header-wrapper' &&
              child.type !== 'footer-wrapper'
          );
          const colElements = [];
          for (const child of colChildren) {
            const childResult = await processBlockNode(child, {}, pageIndex);
            if (Array.isArray(childResult)) {
              colElements.push(...childResult);
            } else if (childResult) {
              colElements.push(childResult);
            }
          }

          columnCells.push(
            new TableCell({
              children:
                colElements.length > 0
                  ? colElements
                  : [new Paragraph({text: ''})],
              width: {
                size:
                  totalExplicit > 0
                    ? (Math.max(
                        0,
                        Number(explicitWidths[columnCells.length]) || 0
                      ) /
                        totalExplicit) *
                      100
                    : 100 / Math.max(1, columnNodes.length),
                type: WidthType.PERCENTAGE
              },
              borders: {
                top: {style: BorderStyle.NONE, size: 0, color: 'auto'},
                bottom: {style: BorderStyle.NONE, size: 0, color: 'auto'},
                left: {style: BorderStyle.NONE, size: 0, color: 'auto'},
                right: {style: BorderStyle.NONE, size: 0, color: 'auto'}
              },
              margins: {left: 120, right: 120}
            })
          );
        }

        return new Table({
          rows: [new TableRow({children: columnCells})],
          width: {size: 100, type: WidthType.PERCENTAGE},
          borders: {
            top: {style: BorderStyle.NONE, size: 0, color: 'auto'},
            bottom: {style: BorderStyle.NONE, size: 0, color: 'auto'},
            left: {style: BorderStyle.NONE, size: 0, color: 'auto'},
            right: {style: BorderStyle.NONE, size: 0, color: 'auto'},
            insideVertical: {style: BorderStyle.NONE, size: 0, color: 'auto'},
            insideHorizontal: {style: BorderStyle.NONE, size: 0, color: 'auto'}
          }
        });

      case 'column':
        const columnElements = [];
        for (const child of node.children || []) {
          const childResult = await processBlockNode(child, {}, pageIndex);
          if (Array.isArray(childResult)) {
            columnElements.push(...childResult);
          } else if (childResult) {
            columnElements.push(childResult);
          }
        }

        return columnElements;

      case 'framework-question':
        const questionText = node.question;
        const typeLabel = node.framework
          ? node.framework.toUpperCase()
          : 'QUESTION';
        const headerColor = getFrameworkColor(node.framework);

        const questionTable = new Table({
          rows: [
            new TableRow({
              children: [
                new TableCell({
                  children: [
                    new Paragraph({
                      children: [
                        new TextRun({
                          text: typeLabel,
                          color: 'FFFFFF',
                          bold: true
                        })
                      ],
                      alignment: AlignmentType.LEFT
                    })
                  ],
                  shading: {fill: sanitizeHexColor(headerColor) ?? '000000'}
                })
              ]
            }),
            new TableRow({
              children: [
                new TableCell({
                  children: [new Paragraph({text: questionText})]
                })
              ]
            })
          ],
          width: {size: 100, type: WidthType.PERCENTAGE}
        });

        return [questionTable, new Paragraph({text: ''})];

      case 'header-wrapper':
      case 'footer-wrapper':
        if (
          node.headerType === 'three-columns' ||
          node.footerType === 'three-columns'
        ) {
          const cells = [];
          for (let i = 0; i < node.children.length; i++) {
            const child = node.children[i];
            const childResult = await processBlockNode(child, {}, pageIndex);
            const contents = Array.isArray(childResult)
              ? childResult
              : [childResult];

            cells.push(
              new TableCell({
                children: contents.filter(Boolean),
                width: {
                  size: 100 / Math.max(1, node.children.length),
                  type: WidthType.PERCENTAGE
                },
                borders: {
                  top: {style: BorderStyle.NONE, size: 0, color: 'auto'},
                  bottom: {style: BorderStyle.NONE, size: 0, color: 'auto'},
                  left: {style: BorderStyle.NONE, size: 0, color: 'auto'},
                  right: {style: BorderStyle.NONE, size: 0, color: 'auto'}
                }
              })
            );
          }

          return [
            new Table({
              rows: [new TableRow({children: cells})],
              width: {size: 100, type: WidthType.PERCENTAGE},
              borders: {
                top: {style: BorderStyle.NONE, size: 0, color: 'auto'},
                bottom: {style: BorderStyle.NONE, size: 0, color: 'auto'},
                left: {style: BorderStyle.NONE, size: 0, color: 'auto'},
                right: {style: BorderStyle.NONE, size: 0, color: 'auto'},
                insideVertical: {
                  style: BorderStyle.NONE,
                  size: 0,
                  color: 'auto'
                },
                insideHorizontal: {
                  style: BorderStyle.NONE,
                  size: 0,
                  color: 'auto'
                }
              }
            }),
            new Paragraph({text: ''})
          ];
        } else if (
          node.headerType === 'banded' ||
          node.footerType === 'banded'
        ) {
          const elements = [];
          for (const child of node.children) {
            // Inherit child text alignment if center
            const childNode = {...child};
            if (!childNode.align) childNode.align = 'center';

            // Enforce white text color for banded elements
            childNode.children = childNode.children?.map((c: any) => ({
              ...c,
              color: '#ffffff'
            }));

            const childResult = await processBlockNode(
              childNode,
              {},
              pageIndex
            );
            if (Array.isArray(childResult)) {
              elements.push(...childResult);
            } else if (childResult) {
              elements.push(childResult);
            }
          }

          return [
            new Table({
              rows: [
                new TableRow({
                  children: [
                    new TableCell({
                      children: elements.filter(Boolean),
                      shading: {fill: sanitizeHexColor('2c3e50')},
                      borders: {
                        top: {style: BorderStyle.NONE, size: 0, color: 'auto'},
                        bottom: {
                          style: BorderStyle.NONE,
                          size: 0,
                          color: 'auto'
                        },
                        left: {style: BorderStyle.NONE, size: 0, color: 'auto'},
                        right: {style: BorderStyle.NONE, size: 0, color: 'auto'}
                      },
                      margins: {
                        top: 144, // 10px in twips (roughly)
                        bottom: 144,
                        left: 288,
                        right: 288
                      }
                    })
                  ]
                })
              ],
              width: {size: 100, type: WidthType.PERCENTAGE},
              borders: {
                top: {style: BorderStyle.NONE, size: 0, color: 'auto'},
                bottom: {style: BorderStyle.NONE, size: 0, color: 'auto'},
                left: {style: BorderStyle.NONE, size: 0, color: 'auto'},
                right: {style: BorderStyle.NONE, size: 0, color: 'auto'},
                insideVertical: {
                  style: BorderStyle.NONE,
                  size: 0,
                  color: 'auto'
                },
                insideHorizontal: {
                  style: BorderStyle.NONE,
                  size: 0,
                  color: 'auto'
                }
              }
            }),
            new Paragraph({text: ''})
          ];
        } else {
          const elements = [];
          for (const child of node.children) {
            const childResult = await processBlockNode(child, {}, pageIndex);
            if (Array.isArray(childResult)) {
              elements.push(...childResult);
            } else if (childResult) {
              elements.push(childResult);
            }
          }

          return elements;
        }

      default:
        // Normal paragraph
        return new Paragraph({
          children: processChildren(node.children, pageIndex),
          alignment: node.align
            ? AlignmentType[
                node.align.toUpperCase() as keyof typeof AlignmentType
              ]
            : inheritedStyle.align
              ? inheritedStyle.align
              : AlignmentType.LEFT,
          spacing: getParagraphSpacing(node, 120)
        });
    }
  };

  // Flatten content if it's paginated and extract real header/footer templates.
  const flatContent: any[] = [];
  let headerTemplate: any = null;
  let footerTemplate: any = null;
  const collectNode = (node: any, pageIndex: number) => {
    if (node.type === 'header-wrapper') {
      if (!headerTemplate) headerTemplate = node;

      return;
    }
    if (node.type === 'footer-wrapper') {
      if (!footerTemplate) footerTemplate = node;

      return;
    }
    flatContent.push({...node, pageIndex});
  };

  (content || []).forEach((node: any, pageIndex: number) => {
    if (node.type === 'page' && Array.isArray(node.children)) {
      node.children.forEach((child: any) => {
        collectNode(child, pageIndex);
      });
    } else {
      collectNode(node, 0);
    }
  });

  if (flatContent.length === 0) {
    flatContent.push({type: 'paragraph', children: [{text: ''}]});
  }

  for (const node of flatContent) {
    const result = await processBlockNode(node, {}, node.pageIndex);
    if (Array.isArray(result)) {
      children.push(...result.filter(Boolean));
    } else if (result) {
      children.push(result);
    }
  }

  const headers: any = {};
  const headerChildren = [...watermarkHeaderChildren];
  if (headerTemplate) {
    const headerResult = await processBlockNode(headerTemplate);
    if (Array.isArray(headerResult))
      headerChildren.push(...headerResult.filter(Boolean));
    else if (headerResult) headerChildren.push(headerResult);
  }
  if (headerChildren.length > 0) {
    headers.default = new Header({children: headerChildren});
  }

  const footers: any = {};
  if (footerTemplate) {
    const footerResult = await processBlockNode(footerTemplate);
    const footerChildren = Array.isArray(footerResult)
      ? footerResult.filter(Boolean)
      : footerResult
        ? [footerResult]
        : [];
    if (footerChildren.length > 0) {
      footers.default = new Footer({children: footerChildren});
    }
  }

  const doc = new Document({
    numbering: {
      config: numberingConfigs
    },
    sections: [
      {
        properties: {
          page: {
            size: {
              width: widthTwips,
              height: heightTwips
            },
            margin: {
              top: inchesToTwip(margins.top),
              right: inchesToTwip(margins.right),
              bottom: inchesToTwip(margins.bottom),
              left: inchesToTwip(margins.left),
              header: Math.min(inchesToTwip(margins.top), 720),
              footer: Math.min(inchesToTwip(margins.bottom), 720)
            }
          }
        },
        headers,
        footers,
        children
      }
    ]
  });

  const blob = await Packer.toBlob(doc);
  saveAs(blob, filename);

  return blob;
};
